import { Select, Text, Tooltip, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Environment } from '@prisma/client';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { currentEnvironmentAtom } from '~/atoms/no-code-atoms';
import { executedVariablesAtom, recorderInitiatorAtom } from '~/atoms/recorder-atoms';
import type { ExecutorVar } from '~/common/vm/protocol';
import { SENSITIVE_VARIABLE_VALUE } from '~/components/settings/Variables';
import { DataTable } from '~/components/tables/common/DataTable';
import { useDataTable } from '~/components/tables/common/useDataTable';
import { localVariableColumns } from '~/components/tables/variable/localVariableColumns';
import { projectVariableColumns } from '~/components/tables/variable/projectVariableColumns';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import { queryParamToString } from '~/logic/query-param';
import { getVariableValue } from '~/server/logic/variable/value';
import type { RouterOutputs } from '~/trpc-extra/api';
import { api } from '~/trpc-extra/api';

const SEARCH_DEBOUNCE_MS = 300;

export function VariablesPanel({
  testContentId,
  showSearch,
  setShowSearch,
}: {
  testContentId: string;
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
}) {
  const router = useRouter();
  const projectId = queryParamToString(router.query.projectId);

  const currentEnvironment = useAtomValue(currentEnvironmentAtom);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  const { data: projectVariables = [] } = api.variable.list.useQuery(
    { projectId: projectId!, environments: [currentEnvironment, 'ALL'] },
    {
      select: (data) =>
        data.map((variable) => ({
          ...variable,
          value: variable.sensitive ? SENSITIVE_VARIABLE_VALUE : getVariableValue(variable.value),
        })),
    },
  );

  // Sort to make the display more consistent
  const localVariablesWithDefinitions = useTestVariables({ testContentId, scopes: new Set(['test']) }).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const executedVariables = useAtomValue(executedVariablesAtom);

  const variableValuesMap = useMemo(
    () => new Map(executedVariables.map((variable) => [variable.name, variable.value])),
    [executedVariables],
  );

  const localVariables = useMemo(
    () =>
      localVariablesWithDefinitions.map((variable) => ({
        ...variable,
        // For defineVariableOp, the definition is stored in the value field (which is a misnomer)
        definition: variable.value || '',
        value: variableValuesMap.get(variable.name),
      })),
    [localVariablesWithDefinitions, variableValuesMap],
  );

  // Filter variables based on search query
  const filteredLocalVariables = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return localVariables;
    }

    const query = debouncedSearchQuery.toLowerCase();
    return localVariables.filter((variable) => {
      const name = variable.name.toLowerCase();
      const definition = getVariableValue(variable.definition).toLowerCase();
      const value = getVariableValue(variable.value)?.toLowerCase() || 'undefined';
      return name.includes(query) || definition.includes(query) || value?.includes(query);
    });
  }, [localVariables, debouncedSearchQuery]);

  const filteredProjectVariables = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return projectVariables;
    }

    const query = debouncedSearchQuery.toLowerCase();
    return projectVariables.filter((variable) => {
      const name = variable.name.toLowerCase();
      const value = variable.value.toLowerCase();
      return name.includes(query) || value.includes(query);
    });
  }, [projectVariables, debouncedSearchQuery]);

  return (
    <VariablePanelTables
      localVariables={filteredLocalVariables}
      projectVariables={filteredProjectVariables}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      showSearch={showSearch}
      setShowSearch={setShowSearch}
      debouncedSearchQuery={debouncedSearchQuery}
    />
  );
}

function VariablePanelTables({
  localVariables,
  projectVariables,
  searchQuery,
  setSearchQuery,
  showSearch,
  setShowSearch,
  debouncedSearchQuery,
}: {
  localVariables: (ExecutorVar & { definition: string })[];
  projectVariables: (RouterOutputs['variable']['list'][number] & { value: string })[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
  debouncedSearchQuery: string;
}) {
  const [localVars, projectVars] = useMemo(() => {
    return [
      localVariables.map((variable) => ({
        ...variable,
        definition: getVariableValue(variable.definition),
        value: getVariableValue(variable.value),
      })),

      projectVariables.map((variable) => ({
        ...variable,
        value: getVariableValue(variable.value),
      })),
    ];
  }, [localVariables, projectVariables]);

  const localVariablesTable = useDataTable({
    columns: localVariableColumns,
    data: localVars,
  });

  const projectVariablesTable = useDataTable({
    columns: projectVariableColumns,
    data: projectVars,
  });

  const [currentEnvironment, setCurrentEnvironment] = useAtom(currentEnvironmentAtom);
  const recorderInitiator = useAtomValue(recorderInitiatorAtom);
  const recorderActive = recorderInitiator !== undefined;

  return (
    <div className="relative h-full w-full border-t bg-[var(--mantine-color-body)] dark:border-neutral-700">
      {!!showSearch && (
        <TextInput
          type="text"
          placeholder="Search variables..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          w={200}
          size="xs"
          autoFocus
          className="absolute right-6 top-2 z-20 shadow-lg"
          leftSection={
            <IconSearch
              size={14}
              className="text-neutral-500"
            />
          }
          rightSection={
            <IconX
              size={14}
              className="cursor-pointer text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              onClick={() => {
                setSearchQuery('');
                setShowSearch(false);
              }}
            />
          }
        />
      )}
      <div className="mb-2 flex flex-col">
        <div className="sticky top-0 z-10 bg-white px-4 py-3 dark:bg-neutral-800">
          <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">Local Variables</Text>
          {localVariables.length > 0 && (
            <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {localVariables.length} variable{localVariables.length !== 1 ? 's' : ''}
            </Text>
          )}
        </div>
        {localVariables.length > 0 ?
          <div className="overflow-hidden rounded-b-lg px-4">
            <DataTable
              table={localVariablesTable}
              classNames={{
                header: 'bg-gray-50 dark:bg-neutral-800',
                pinnedCell: 'bg-gray-50 dark:bg-neutral-800',
              }}
              emptyMessage={<div />}
            />
          </div>
        : <div className="rounded-b-lg px-4 py-2">
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {debouncedSearchQuery.trim() ?
                `No local variables found matching "${debouncedSearchQuery}"`
              : 'No local variables defined'}
            </Text>
          </div>
        }
      </div>

      <div className="flex flex-col">
        <div className="sticky top-0 z-10 bg-white px-4 py-3 dark:bg-neutral-800">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">Env Variables</Text>
              {projectVariables.length > 0 && (
                <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {projectVariables.length} variable{projectVariables.length !== 1 ? 's' : ''} available in the selected
                  environment
                </Text>
              )}
            </div>
            <Tooltip
              label="Variables from this environment will be available when you record new actions"
              openDelay={500}
            >
              <Select
                size="sm"
                label="Environment"
                value={currentEnvironment}
                onChange={(value) => {
                  setCurrentEnvironment(
                    // Condition is here to make TS happy, otherwise I need to use `as Environment` which is not safe
                    value === 'PRODUCTION' || value === 'STAGING' || value === 'DEVELOPMENT' ?
                      (value satisfies Environment)
                    : 'PRODUCTION',
                  );
                  if (recorderActive) {
                    notifications.show({
                      message: 'Environment updated, restart the recorder to see the effect',
                      color: 'yellow',
                    });
                  }
                }}
                data={[
                  { value: 'PRODUCTION' satisfies Environment, label: 'Production' },
                  { value: 'STAGING' satisfies Environment, label: 'Staging' },
                  { value: 'DEVELOPMENT' satisfies Environment, label: 'Development' },
                ]}
                classNames={{
                  input:
                    'dark:bg-[var(--mantine-color-dark-6)] dark:border-[var(--mantine-color-dark-4)] dark:text-[var(--mantine-color-gray-4)]',
                }}
              />
            </Tooltip>
          </div>
        </div>
        {projectVariables.length > 0 ?
          <div className="overflow-hidden rounded-b-lg">
            <DataTable
              className="border-0 bg-inherit"
              classNames={{
                header: 'bg-gray-50 dark:bg-neutral-800',
                pinnedCell: 'bg-gray-50 dark:bg-neutral-800',
              }}
              table={projectVariablesTable}
              emptyMessage={<div />}
            />
          </div>
        : <div className="rounded-b-lg px-4 py-2">
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {debouncedSearchQuery.trim() ?
                `No env variables found matching "${debouncedSearchQuery}"`
              : 'No env variables defined'}
            </Text>
          </div>
        }
      </div>
    </div>
  );
}
